import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GroupIcon from '@mui/icons-material/Group';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import PowerIcon from '@mui/icons-material/Power';
import SupportIcon from '@mui/icons-material/Support';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { 
  getProjectStakeholders,
  saveStakeholder,
  updateStakeholderSummary,
  removeStakeholderFromProject,
  getDefaultStakeholderData,
  stakeholderFieldOptions,
  validateStakeholder
} from '../../utils/stakeholderService';
import StakeholderImportDialog from '../../components/StakeholderImportDialog';
import { getSOWInsightsForPrepare } from '../../utils/aiService';
import { apiClient } from '../../utils/api';
import StakeholderDialog from '../../components/StakeholderDialog';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

// Stakeholder role icons
const roleIcons = {
  'Executive Sponsor': <BusinessIcon />,
  'Project Sponsor': <PowerIcon />,
  'Business Owner': <BusinessIcon />,
  'End User': <PersonIcon />,
  'IT Team': <SupportIcon />,
  'Project Manager': <PersonIcon />,
  'Subject Matter Expert': <PersonIcon />
};

// Impact colors
const impactColors = {
  'High': 'error',
  'Medium': 'warning',
  'Low': 'success'
};

// Influence colors
const influenceColors = {
  'High': 'primary',
  'Medium': 'info',
  'Low': 'default'
};

// Sentiment colors
const sentimentColors = {
  'Positive': 'success',
  'Neutral': 'info',
  'Negative': 'error'
};

const StakeholderAnalysisPage = () => {
  const { projectId } = useParams();
  const { project, refreshOverview } = useOutletContext();
  
  const [stakeholderData, setStakeholderData] = useState({
    summary: '',
    stakeholders: []
  });
  const [fetchConfirmOpen, setFetchConfirmOpen] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [sourceDocuments, setSourceDocuments] = useState([]);
  const [primarySource, setPrimarySource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingSummary, setEditingSummary] = useState('');

  // Project details edit states
  const [editProjectDetails, setEditProjectDetails] = useState(false);
  const [projectDetails, setProjectDetails] = useState({
    customer_number: '',
    cpi_project_number: ''
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogItem, setDialogItem] = useState(getDefaultStakeholderData());
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // View states
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Load stakeholder data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadStakeholderDataFromDB();
    }
  }, [projectId]);

  // Load project details when project changes
  useEffect(() => {
    if (project) {
      setProjectDetails({
        customer_number: project.customer_number || '',
        cpi_project_number: project.cpi_project_number || ''
      });
    }
  }, [project]);
  
  // Load stakeholder data from database
  const loadStakeholderDataFromDB = async () => {
    setLoading(true);
    try {
      const response = await getProjectStakeholders(projectId);
      
      if (response.success) {
        setStakeholderData({
          summary: response.summary || '',
          stakeholders: response.stakeholders || []
        });
        setEditingSummary(response.summary || '');
      } else {
        // Set empty state on error
        const emptyState = {
          summary: '',
          stakeholders: []
        };
        setStakeholderData(emptyState);
        setEditingSummary('');
      }
    } catch (error) {
      console.error('Error loading stakeholder data from database:', error);
      // Set empty state on error
      const emptyState = {
        summary: '',
        stakeholders: []
      };
      setStakeholderData(emptyState);
      setEditingSummary('');
    } finally {
      setLoading(false);
    }
  };
  
  // Get the SOW document from the project
  const getSowDocument = () => {
    if (project && project.documents && project.documents.sow && project.documents.sow.length > 0) {
      return project.documents.sow[0];
    }
    return null;
  };
  
  // Fetch stakeholder analysis from project documents and save to database
  const fetchStakeholdersFromSOW = async () => {
    if (!projectId) return;
    
    // Check if we have a SOW document
    const sowDoc = getSowDocument();
    if (!sowDoc) {
      setSnackbar({
        open: true,
        message: 'No project documents found. Please upload project documents first.',
        severity: 'warning'
      });
      return;
    }
    
    setAiLoading(true);
    setAiResponse(null);
    
    try {
      // Use AI insights to generate stakeholder data
      const sowInsightsResult = await getSOWInsightsForPrepare(projectId, 'stakeholder_analysis');
      
      if (sowInsightsResult.success && sowInsightsResult.data) {
        // Convert the old format to new stakeholder objects if needed
        const stakeholders = sowInsightsResult.data.stakeholders || [];
        const convertedStakeholders = stakeholders.map(stakeholder => ({
          ...getDefaultStakeholderData(),
          ...stakeholder,
          stakeholder_id: '' // Will be auto-generated when saved
        }));
        
        // Update summary
        if (sowInsightsResult.data.summary) {
          await updateStakeholderSummary(projectId, sowInsightsResult.data.summary);
        }
        
        // Save each stakeholder
        const savedStakeholders = [];
        for (const stakeholder of convertedStakeholders) {
          const saveResult = await saveStakeholder(projectId, stakeholder);
          if (saveResult.success) {
            savedStakeholders.push(saveResult.stakeholder);
          }
        }
        
        // Reload data from database
        await loadStakeholderDataFromDB();
        
        // Store source information
        if (sowInsightsResult.source) {
          setPrimarySource(sowInsightsResult.source);
        }
        
        // Store raw content for reference if available
        if (sowInsightsResult.rawContent) {
          setAiResponse(sowInsightsResult.rawContent);
        }
        
        setSnackbar({
          open: true,
          message: `Successfully generated ${savedStakeholders.length} stakeholders from project documents.`,
          severity: 'success'
        });
        
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to extract stakeholder analysis from project documents.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error fetching stakeholder analysis from project documents:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error fetching stakeholder analysis from project documents.',
        severity: 'error'
      });
    } finally {
      setAiLoading(false);
    }
  };
  
  // Enter edit mode
  const handleEdit = () => {
    setEditingSummary(stakeholderData.summary);
    setEditMode(true);
  };
  
  // Cancel edit mode
  const handleCancel = () => {
    setEditingSummary(stakeholderData.summary);
    setEditMode(false);
  };
  
  // Save summary changes
  const handleSaveSummary = async () => {
    setLoading(true);
    try {
      const result = await updateStakeholderSummary(projectId, editingSummary);
      
      if (result.success) {
        setStakeholderData(prev => ({
          ...prev,
          summary: editingSummary
        }));
        setEditMode(false);
        if (refreshOverview) refreshOverview();
        
        setSnackbar({
          open: true,
          message: 'Summary saved successfully.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Error saving summary.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving summary:', error);
      setSnackbar({
        open: true,
        message: 'Error saving summary. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Download Excel report for all stakeholder details
  const downloadExcelReport = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/projects/${projectId}/stakeholders/export`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Stakeholder_Onboarding_Checklist_${project?.name || 'Project'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'Excel report downloaded successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error downloading Excel report:', error);
      setSnackbar({
        open: true,
        message: 'Error downloading Excel report. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle summary change
  const handleSummaryChange = (e) => {
    setEditingSummary(e.target.value);
  };

  // Handle project details edit
  const handleEditProjectDetails = () => {
    setEditProjectDetails(true);
  };

  // Handle project details cancel
  const handleCancelProjectDetails = () => {
    if (project) {
      setProjectDetails({
        customer_number: project.customer_number || '',
        cpi_project_number: project.cpi_project_number || ''
      });
    }
    setEditProjectDetails(false);
  };

  // Handle project details change
  const handleProjectDetailsChange = (field, value) => {
    setProjectDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save project details
  const handleSaveProjectDetails = async () => {
    setLoading(true);
    try {
      const response = await apiClient.put(`/projects/${projectId}/project-details`, projectDetails);

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: 'Project details saved successfully.',
          severity: 'success'
        });
        setEditProjectDetails(false);
        if (refreshOverview) refreshOverview();
      } else {
        setSnackbar({
          open: true,
          message: response.data.error || 'Error saving project details.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving project details:', error);
      setSnackbar({
        open: true,
        message: 'Error saving project details. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Open add/edit dialog
  const handleOpenDialog = (index = null) => {
    setDialogItemIndex(index);
    setValidationErrors([]);
    
    if (index !== null) {
      // Edit existing item
      setDialogItem({...stakeholderData.stakeholders[index]});
    } else {
      // Add new item
      setDialogItem(getDefaultStakeholderData());
    }
    
    setOpenDialog(true);
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogItem(getDefaultStakeholderData());
    setValidationErrors([]);
  };
  
  // Handle dialog input change
  const handleDialogInputChange = (e) => {
    const { name, value } = e.target;
    setDialogItem(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };
  
  // Save dialog item
  const handleSaveDialogItem = async () => {
    // Validate the stakeholder data
    const validation = validateStakeholder(dialogItem);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }
    
    setLoading(true);
    try {
      const result = await saveStakeholder(projectId, dialogItem);
      
      if (result.success) {
        // Reload stakeholder data
        await loadStakeholderDataFromDB();

        const autoMsg = result.auto_assigned_modules?.length
          ? ` Auto-assigned to workshop module: ${result.auto_assigned_modules.join(', ')}.`
          : '';
        setSnackbar({
          open: true,
          message: `Stakeholder ${dialogItemIndex === null ? 'created' : 'updated'} successfully.${autoMsg}`,
          severity: 'success'
        });
        
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Error saving stakeholder.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving stakeholder:', error);
      setSnackbar({
        open: true,
        message: 'Error saving stakeholder. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Delete item
  const handleDeleteItem = async (stakeholder) => {
    if (!stakeholder.stakeholder_id) return;
    
    setLoading(true);
    try {
      const result = await removeStakeholderFromProject(projectId, stakeholder.stakeholder_id);
      
      if (result.success) {
        // Reload stakeholder data
        await loadStakeholderDataFromDB();
        
        setSnackbar({
          open: true,
          message: 'Stakeholder removed successfully.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Error removing stakeholder.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error removing stakeholder:', error);
      setSnackbar({
        open: true,
        message: 'Error removing stakeholder. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Get initial for avatar
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };
  
  // Determine avatar background color based on influence
  const getAvatarColor = (influence) => {
    switch(influence) {
      case 'High':
        return { bgcolor: 'primary.main' };
      case 'Medium':
        return { bgcolor: 'secondary.main' };
      case 'Low':
        return { bgcolor: 'grey.500' };
      default:
        return { bgcolor: 'grey.500' };
    }
  };
  
  // Get sentiment icon
  const getSentimentIcon = (sentiment) => {
    switch(sentiment) {
      case 'Positive':
        return <ThumbUpIcon color="success" />;
      case 'Negative':
        return <ThumbDownIcon color="error" />;
      default:
        return <InfoIcon color="action" />;
    }
  };

  // Get key role from stakeholders list (matches template_service.py logic)
  const getKeyRole = (roleType) => {
    const stakeholders = stakeholderData.stakeholders;

    switch(roleType) {
      case 'seleccion_pm':
        const seleccionPm = stakeholders.find(s => {
          const role = (s.role || '').toLowerCase();
          const org = (s.organization || '').toLowerCase();
          const isSeleccion = org.includes('seleccion') || org.includes('si') || role.startsWith('seleccion');
          return isSeleccion && (role.includes('project manager') || role.includes('pm'));
        });
        return seleccionPm?.name || 'Not assigned';

      case 'customer_pm':
        const customerPm = stakeholders.find(s => {
          const role = (s.role || '').toLowerCase();
          const org = (s.organization || '').toLowerCase();
          const isSeleccion = org.includes('seleccion') || org.includes('si') || role.startsWith('seleccion');
          return !isSeleccion && (role.includes('project manager') || role.includes('pm') || role.includes('project coordinator'));
        });
        return customerPm?.name || 'Not assigned';

      case 'customer_sponsor':
        const customerSponsor = stakeholders.find(s => {
          const role = (s.role || '').toLowerCase();
          return role.includes('sponsor') || role.includes('executive sponsor');
        });
        return customerSponsor?.name || 'Not assigned';

      case 'seleccion_delivery_exec':
        const seleccionExec = stakeholders.find(s => {
          const role = (s.role || '').toLowerCase();
          const org = (s.organization || '').toLowerCase();
          const isSeleccion = org.includes('seleccion') || org.includes('si') || role.startsWith('seleccion');
          return isSeleccion && (role.includes('delivery executive') || role.includes('delivery lead') || role.includes('executive'));
        });
        return seleccionExec?.name || 'Not assigned';

      default:
        return 'Not assigned';
    }
  };

  // Render stakeholder cards
  const renderStakeholderCards = () => {
    return (
      <Grid container spacing={2}>
        {stakeholderData.stakeholders.map((stakeholder, index) => (
          <Grid item xs={12} md={6} lg={4} key={stakeholder.stakeholder_id || index}>
            <Card 
              variant="outlined" 
              sx={{ 
                height: '100%',
                borderLeft: 4, 
                borderColor: `${impactColors[stakeholder.impact || 'Medium']}.main`,
                position: 'relative'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={getAvatarColor(stakeholder.influence || 'Medium')}>
                    {getInitial(stakeholder.name || '')}
                  </Avatar>
                  <Box sx={{ ml: 1, flexGrow: 1 }}>
                    <Typography variant="h6" component="div">
                      {stakeholder.name || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stakeholder.stakeholder_id}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(index)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteItem(stakeholder)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Chip 
                    icon={roleIcons[stakeholder.role || 'Business Owner'] || <PersonIcon />}
                    label={stakeholder.role || 'Business Owner'} 
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1, mr: 1 }}
                  />
                  {stakeholder.organization && (
                    <Chip 
                      icon={<BusinessIcon />}
                      label={stakeholder.organization} 
                      size="small"
                      variant="outlined"
                      sx={{ mb: 1, mr: 1 }}
                    />
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Impact: ${stakeholder.impact || 'Medium'}`}
                    color={impactColors[stakeholder.impact || 'Medium']} 
                    size="small"
                  />
                  <Chip 
                    label={`Influence: ${stakeholder.influence || 'Medium'}`}
                    color={influenceColors[stakeholder.influence || 'Medium']} 
                    size="small"
                  />
                  <Chip 
                    icon={getSentimentIcon(stakeholder.sentiment || 'Neutral')}
                    label={stakeholder.sentiment || 'Neutral'}
                    color={sentimentColors[stakeholder.sentiment || 'Neutral']} 
                    size="small"
                  />
                </Box>
                
                {stakeholder.main_email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{stakeholder.main_email}</Typography>
                  </Box>
                )}
                
                {stakeholder.phone_number && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{stakeholder.phone_number}</Typography>
                  </Box>
                )}
                
                {(stakeholder.start_date || stakeholder.end_date) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {stakeholder.start_date && new Date(stakeholder.start_date).toLocaleDateString()}
                      {stakeholder.start_date && stakeholder.end_date && ' - '}
                      {stakeholder.end_date && new Date(stakeholder.end_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {/* Add new stakeholder card */}
        <Grid item xs={12} md={6} lg={4}>
          <Card 
            variant="outlined" 
            sx={{ 
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
            onClick={() => handleOpenDialog()}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <AddIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" color="primary.main">
                Add Stakeholder
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render stakeholder table
  const renderStakeholderTable = () => {
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Impact</TableCell>
              <TableCell>Influence</TableCell>
              <TableCell>Sentiment</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stakeholderData.stakeholders.map((stakeholder, index) => (
              <TableRow key={stakeholder.stakeholder_id || index}>
                <TableCell>
                  <Chip 
                    label={stakeholder.stakeholder_id} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {stakeholder.name}
                    </Typography>
                    {stakeholder.preferred_name && (
                      <Typography variant="caption" color="text.secondary">
                        ({stakeholder.preferred_name})
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{stakeholder.role}</TableCell>
                <TableCell>{stakeholder.organization}</TableCell>
                <TableCell>
                  <Chip 
                    label={stakeholder.impact} 
                    color={impactColors[stakeholder.impact || 'Medium']} 
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={stakeholder.influence} 
                    color={influenceColors[stakeholder.influence || 'Medium']} 
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    icon={getSentimentIcon(stakeholder.sentiment)}
                    label={stakeholder.sentiment} 
                    color={sentimentColors[stakeholder.sentiment || 'Neutral']} 
                    size="small"
                  />
                </TableCell>
                <TableCell>{stakeholder.main_email}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(index)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteItem(stakeholder)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const hasExistingTeam =
    Boolean(stakeholderData.summary) ||
    stakeholderData.stakeholders.length > 0;
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <SectionTitle variant="h5">
            <GroupIcon />
            Project Team
          </SectionTitle>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editMode ? (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleSaveSummary}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="error"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    hasExistingTeam ? setFetchConfirmOpen(true) : fetchStakeholdersFromSOW();
                  }}
                  disabled={loading || aiLoading || !getSowDocument()}
                  sx={{ mr: 1 }}
                >
                  {loading
                    ? 'Loading...'
                    : aiLoading
                    ? hasExistingTeam ? 'Regenerating...' : 'Fetching...'
                    : hasExistingTeam ? 'Regenerate from Project Documents' : 'Fetch from Project Documents'}
                </Button>
                <Button
                  startIcon={<DownloadIcon />}
                  variant="contained"
                  color="success"
                  onClick={downloadExcelReport}
                  disabled={loading || stakeholderData.stakeholders.length === 0}
                  sx={{ mr: 1 }}
                >
                  {loading ? 'Downloading...' : 'Excel Report'}
                </Button>
                <Button
                  startIcon={<UploadFileIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={() => setImportDialogOpen(true)}
                  sx={{ mr: 1 }}
                >
                  Import Stakeholder List
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleEdit}
                >
                  Edit Summary
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" paragraph>
          Identify and analyze key project stakeholders and their involvement in the SAP implementation project.
        </Typography>
        
        {!getSowDocument() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No project documents found. Upload project documents to enable AI-assisted content generation.
          </Alert>
        )}

        {/* Key Project Roles Section */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'action.hover' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1, color: '#F5821F' }} />
            Key Project Roles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            These roles are automatically populated from the stakeholders below based on their role and organization.
            Add or edit stakeholders to update these fields.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SELECCION Project Manager"
                value={getKeyRole('seleccion_pm')}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                size="small"
                helperText="Organization: SELECCION"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Project Manager"
                value={getKeyRole('customer_pm')}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                size="small"
                helperText="Organization: Customer"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Project Sponsor"
                value={getKeyRole('customer_sponsor')}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                size="small"
                helperText="Role: Sponsor"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SELECCION Delivery Executive"
                value={getKeyRole('seleccion_delivery_exec')}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
                size="small"
                helperText="Organization: SELECCION, Role: Delivery Executive"
              />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              💡 These key roles are used to auto-populate project templates. Ensure stakeholders have correct
              <strong> Role</strong> and <strong> Organization</strong> values for accurate template generation.
            </Typography>
          </Alert>
        </Paper>

        {/* Project Details Section */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, backgroundColor: 'action.hover' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1, color: '#F5821F' }} />
              Project Details
            </Typography>
            {editProjectDetails ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleSaveProjectDetails}
                  disabled={loading}
                  size="small"
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="error"
                  onClick={handleCancelProjectDetails}
                  size="small"
                >
                  Cancel
                </Button>
              </Box>
            ) : (
              <Button
                startIcon={<EditIcon />}
                variant="outlined"
                color="primary"
                onClick={handleEditProjectDetails}
                size="small"
              >
                Edit
              </Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            These fields can be extracted from project documents or edited manually. They are used in templates and reports.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Customer Name"
                value={project?.customer_name || '—'}
                disabled
                variant="outlined"
                size="small"
                helperText="Assigned from customer in project settings"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Customer Number"
                value={projectDetails.customer_number}
                onChange={(e) => handleProjectDetailsChange('customer_number', e.target.value)}
                disabled={!editProjectDetails}
                variant="outlined"
                size="small"
                helperText="Customer account number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CPI Project Number"
                value={projectDetails.cpi_project_number}
                onChange={(e) => handleProjectDetailsChange('cpi_project_number', e.target.value)}
                disabled={!editProjectDetails}
                variant="outlined"
                size="small"
                helperText="CPI project identifier"
              />
            </Grid>
          </Grid>
        </Paper>

        {aiLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Summary */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Stakeholder Management Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {editMode ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={editingSummary}
                onChange={handleSummaryChange}
                placeholder="Enter an overview of the stakeholder management approach"
              />
            ) : (
              <Typography variant="body1" paragraph>
                {stakeholderData.summary || 'No summary available. Click "Edit Summary" to add one.'}
              </Typography>
            )}
          </CardContent>
        </Card>
        
        {/* View Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Stakeholders ({stakeholderData.stakeholders.length})
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={viewMode === 'table'}
                onChange={(e) => setViewMode(e.target.checked ? 'table' : 'cards')}
                icon={<ViewModuleIcon />}
                checkedIcon={<ViewListIcon />}
              />
            }
            label={viewMode === 'table' ? 'Table View' : 'Card View'}
          />
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Stakeholders Display */}
        {viewMode === 'cards' ? renderStakeholderCards() : renderStakeholderTable()}
        
        {stakeholderData.stakeholders.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No stakeholders added yet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Add stakeholders manually or use "Fetch from Project Documents" to generate them automatically.
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => handleOpenDialog()}
            >
              Add First Stakeholder
            </Button>
          </Box>
        )}
      </Paper>
      
      {/* AI Response Accordion (if available) */}
      {aiResponse && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>AI-Generated Content (Raw Response)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {aiResponse}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Import Dialog */}
      <SafetyConfirmDialog
        open={fetchConfirmOpen}
        title={hasExistingTeam ? 'Regenerate Project Team from Project Documents?' : 'Fetch Project Team from Project Documents?'}
        warning={hasExistingTeam ? 'The existing Project Team summary will be replaced, and generated stakeholders will be added.' : 'Project Team will be generated from Project Documents.'}
        paragraphs={[
          hasExistingTeam
            ? 'This action will use AI to regenerate the Project Team summary and add generated stakeholders. Existing stakeholders will remain, and repeated generation may create duplicates.'
            : 'This action will use AI to fetch the Project Team summary and stakeholders from Project Documents.',
          ...(hasExistingTeam ? [{ text: 'Download the existing Excel Report first if you want to retain a backup.', secondary: true }] : [])
        ]}
        confirmLabel={hasExistingTeam ? 'Yes, Regenerate Team' : 'Yes, Fetch Team'}
        loading={aiLoading}
        onCancel={() => setFetchConfirmOpen(false)}
        onConfirm={() => { setFetchConfirmOpen(false); fetchStakeholdersFromSOW(); }}
      />

      <StakeholderImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        projectId={projectId}
        projectName={project?.name}
        onImported={(result) => {
          setImportDialogOpen(false);
          loadStakeholderDataFromDB();
          const autoMsg = result?.auto_assigned_modules?.length
            ? ` Auto-assigned to workshop module(s): ${result.auto_assigned_modules.join(', ')}.`
            : '';
          const parts = [];
          if (result?.created?.length) parts.push(`${result.created.length} added`);
          if (result?.updated?.length) parts.push(`${result.updated.length} updated`);
          const baseMsg = parts.length ? `Stakeholders imported: ${parts.join(', ')}.` : 'Stakeholders imported successfully!';
          setSnackbar({ open: true, message: baseMsg + autoMsg, severity: 'success' });
        }}
      />

      {/* Stakeholder Dialog */}
      <StakeholderDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSaveDialogItem}
        stakeholder={dialogItem}
        onChange={handleDialogInputChange}
        validationErrors={validationErrors}
        loading={loading}
        isEdit={dialogItemIndex !== null}
      />
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StakeholderAnalysisPage;
